/**
 * The ONE date formatter in this app.
 *
 * sp-migrate declares `formatDate` four times, in four files, with four
 * different option sets and no date library, so the same timestamp renders
 * differently on two screens. Everything here takes `string | null | undefined`
 * — the API sends RFC3339 strings and sends null for "this has not happened" —
 * and returns the `absent` marker for null rather than a fabricated date.
 */

/** What every formatter renders when the timestamp is absent or unparseable. */
export const ABSENT = "—"; // em dash

function parse(value: string | null | undefined): Date | null {
  if (value === null || value === undefined || value === "") return null;
  const d = new Date(value);
  return Number.isNaN(d.getTime()) ? null : d;
}

/** Date only: 3 Sep 2026. */
export function formatDate(value: string | null | undefined, absent = ABSENT): string {
  const d = parse(value);
  if (d === null) return absent;
  return new Intl.DateTimeFormat(undefined, {
    year: "numeric",
    month: "short",
    day: "numeric",
  }).format(d);
}

/** Date and time to the minute: 3 Sep 2026, 11:16. */
export function formatDateTime(value: string | null | undefined, absent = ABSENT): string {
  const d = parse(value);
  if (d === null) return absent;
  return new Intl.DateTimeFormat(undefined, {
    year: "numeric",
    month: "short",
    day: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  }).format(d);
}

/** Date and time to the second, for audit and evidence trails. */
export function formatTimestamp(value: string | null | undefined, absent = ABSENT): string {
  const d = parse(value);
  if (d === null) return absent;
  return new Intl.DateTimeFormat(undefined, {
    year: "numeric",
    month: "short",
    day: "numeric",
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
  }).format(d);
}

/** The machine-readable value for a <time dateTime=…> attribute. */
export function toISO(value: string | null | undefined): string | undefined {
  const d = parse(value);
  return d === null ? undefined : d.toISOString();
}

const UNITS: Array<[Intl.RelativeTimeFormatUnit, number]> = [
  ["year", 365 * 24 * 3600],
  ["month", 30 * 24 * 3600],
  ["week", 7 * 24 * 3600],
  ["day", 24 * 3600],
  ["hour", 3600],
  ["minute", 60],
  ["second", 1],
];

/**
 * "4 minutes ago" / "in 3 days". `now` is injectable so tests do not depend on
 * the wall clock.
 */
export function formatRelative(
  value: string | null | undefined,
  now: Date = new Date(),
  absent = ABSENT,
): string {
  const d = parse(value);
  if (d === null) return absent;
  const deltaSeconds = (d.getTime() - now.getTime()) / 1000;
  const abs = Math.abs(deltaSeconds);
  const fmt = new Intl.RelativeTimeFormat(undefined, { numeric: "auto" });
  for (const [unit, seconds] of UNITS) {
    if (abs >= seconds || unit === "second") {
      return fmt.format(Math.round(deltaSeconds / seconds), unit);
    }
  }
  return absent;
}

/**
 * A duration in SECONDS as a compact human string ("2 h 14 min").
 *
 * Takes `number | null` because the API's `data_age_seconds` is null when the
 * age is UNKNOWN — and unknown is not the same as current, so null must not
 * render as "0 s".
 */
export function formatDuration(seconds: number | null | undefined, absent = ABSENT): string {
  if (seconds === null || seconds === undefined || !Number.isFinite(seconds)) return absent;
  const total = Math.max(0, Math.round(seconds));
  if (total < 60) return `${total} s`;
  const minutes = Math.floor(total / 60);
  if (minutes < 60) return `${minutes} min`;
  const hours = Math.floor(minutes / 60);
  const remMinutes = minutes % 60;
  if (hours < 24) return remMinutes === 0 ? `${hours} h` : `${hours} h ${remMinutes} min`;
  const days = Math.floor(hours / 24);
  const remHours = hours % 24;
  return remHours === 0 ? `${days} d` : `${days} d ${remHours} h`;
}

/** Elapsed time between two timestamps, either of which may be absent. */
export function formatElapsed(
  from: string | null | undefined,
  to: string | null | undefined,
  absent = ABSENT,
): string {
  const a = parse(from);
  const b = parse(to);
  if (a === null || b === null) return absent;
  return formatDuration((b.getTime() - a.getTime()) / 1000, absent);
}
